import React, { useState } from 'react';
import dayjs from 'dayjs';
import { DateFormat } from 'constants/DateFormat';
import { ISession } from 'interfaces/session/ISession';
import { FilterChip, ColumnItems, FilterChips, FiltersData } from './TableFilters';
import FilterType, { Filter } from './FilterType';
import { SessionFilters } from './SessionFilters';
import { IFilterableStore } from 'interfaces/IFilterableStore';

interface TFProps {
  store: IFilterableStore<ISession>;
}

const SessionTableFilters = (props: TFProps): FiltersData => {
  const { store: s } = props;
  const [textFilter, setTextFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();

  const filters: FilterType[] = [
    {
      type: Filter.DateRange,
      columnNumber: -1, // Temporary hide date filters
      id: SessionFilters.LocalStartTime,
      chipDescription: 'Date range',
      startDateProps: {
        label: 'Select start date',
        value: startDate,
        maxDate: endDate ?? undefined,
        onChange: (e: Date | null): void => setStartDate(e),
      },
      endDateProps: {
        label: 'Select end date',
        value: endDate,
        minDate: startDate ?? undefined,
        onChange: (e: Date | null): void => setEndDate(e),
      },
      onSave: (): void => {
        if (startDate && endDate) {
          const shortStartDate = new Date(startDate).toISOString().split('T')[0];
          const shortEndDate = new Date(endDate).toISOString().split('T')[0];
          s.addFilter({
            id: SessionFilters.LocalStartTime,
            chipDescription: `Period between: ${shortStartDate} - ${shortEndDate}`,
            value: `${dayjs(new Date(startDate)).format(DateFormat.queryFilterFormat)} ${dayjs(new Date(endDate)).format(
              DateFormat.queryFilterFormat
            )}`,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.DateRange,
          });
          setStartDate(null);
          setEndDate(null);
        }
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.Venue,
      columnNumber: 1,
      chipDescription: ['Venue name'],
      inputProps: [
        {
          label: 'Search for venue',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.Venue,
            chipDescription: `Venue name: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.Field,
      columnNumber: 2,
      chipDescription: ['Field name'],
      inputProps: [
        {
          label: 'Search for field(s)',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.Field,
            chipDescription: `Field name: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.HomeTeam,
      columnNumber: 3,
      chipDescription: ['Home team name'],
      inputProps: [
        {
          label: 'Search for home team(s)',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.HomeTeam,
            chipDescription: `Home team: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.Level,
      columnNumber: 4,
      chipDescription: ['Level'],
      inputProps: [
        {
          label: 'Search for level',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.Level,
            chipDescription: `Level: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.League,
      columnNumber: 5,
      chipDescription: ['League'],
      inputProps: [
        {
          label: 'Search for league',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.League,
            chipDescription: `League: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
    {
      type: Filter.Textfields,
      id: SessionFilters.GameReference,
      columnNumber: 6,
      chipDescription: ['Game ref.'],
      inputProps: [
        {
          label: 'Search for game ref.',
          defaultValue: textFilter,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => setTextFilter(e.currentTarget.value),
        },
      ],
      onSave: () => {
        if (textFilter.length > 0)
          s.addFilter({
            id: SessionFilters.GameReference,
            chipDescription: `Game ref.: ${textFilter}`,
            value: textFilter,
            onRemove: (i) => s.removeFilter(i),
            type: Filter.Textfields,
          });
        setTextFilter('');
      },
    },
  ];

  return {
    current: FilterChips(s.filters),
    contentIcons: ColumnItems(filters),
  };
};

export default SessionTableFilters;
